import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import { invalid } from '../validation.ts';
import {
  errorText,
  formatError,
  result,
  validation,
  validationError,
} from './shared.ts';

interface NavigatorWithGpu {
  readonly gpu?: {
    requestAdapter(): Promise<{
      readonly info?: Record<string, unknown>;
      readonly features: Set<string>;
      readonly limits: Record<string, number>;
      requestDevice(): Promise<{
        readonly lost: Promise<Record<string, unknown>>;
        destroy(): void;
      }>;
    } | null>;
  };
}

async function adapterInfo(): Promise<{
  adapter: Awaited<
    ReturnType<NonNullable<NavigatorWithGpu['gpu']>['requestAdapter']>
  >;
  info: Record<string, unknown>;
}> {
  const gpu = (navigator as unknown as NavigatorWithGpu).gpu;
  if (!gpu) {
    return { adapter: null, info: {} };
  }
  const adapter = await gpu.requestAdapter();
  return { adapter, info: adapter?.info ?? {} };
}

async function collect(): Promise<RawData> {
  const { adapter, info } = await adapterInfo();
  return adapter ? { vendor: info.vendor ?? null } : { available: false };
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  return rawData.available == false
    ? 'Unavailable'
    : String(rawData.vendor ?? '');
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return String(rawData.vendor)
    ? validation([])
    : invalid('WebGPU vendor is unavailable');
}

export const webGpuVendorTest: TestDefinition = {
  id: 'webgpuVendor',
  title: 'WebGPU vendor',
  category: 'shared',
  format,
  run,
  validate,
};
